/**
 * `<StateTimeline/>` — the B3.2 "state over time" strip: a compact horizontal
 * band showing how one node moved through the four factual states, with date
 * ticks, state-change dots, and per-span state labels. Rendered above the
 * B3.1 feed inside the drawer's History tab (its faithful home — both read the
 * same event stream).
 *
 * Issue #763 (Phase 3, temporal frontend). Faithful to
 * `B3-2-state-timeline.svg`: a white card on mint, "'<title>' — state over
 * time" heading, a horizontal axis with an arrowhead, grey/indigo state bands
 * coloured by `TASK_STATES`, event dots, "created"/"started"/… captions above,
 * and state labels below the band.
 *
 * Brand firewall (§A9): the axis is *the node's* trajectory — the work, never
 * the worker. No actor names, counts, rates, or rankings appear here; the
 * model (`buildStateTimeline`) carries only state spans + factual captions.
 *
 * Editorial re-skin (#1043, Wave 2): theme-aware. The SAME `buildStateTimeline`
 * model + SVG geometry + `data-testid`s are kept; the editorial fork only
 * RE-SKINS — bands/dots/labels recoloured through `theme.stateStyle` (warm
 * tints, ⚠ amber), a serif italic "<title> — state over time" heading, mono
 * date ticks, and the wireframe's two "READ THIS AS" (sage-bordered, factual) /
 * "NEVER AS" (amber, struck-through) framing cards below the axis. LEGACY keeps
 * the shipped SVG strip, byte-identical.
 */

import type { NodeHistoryItem } from "../api/useNodeHistory";
import { useMapTheme } from "../theme/MapThemeContext";
import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import type { TaskState } from "../tree/state";
import { buildStateTimeline } from "./buildStateTimeline";
import { formatDateTick } from "./formatTime";

// SVG geometry (matches the gallery's 760×180 strip, axis x∈[44,716] y=110).
const W = 712;
const H = 140;
const AXIS_X0 = 36;
const AXIS_X1 = 668;
const AXIS_Y = 96;
const BAND_Y = 81;
const BAND_H = 14;

export interface StateTimelineProps {
  nodeTitle: string;
  events: NodeHistoryItem[];
  /** Injected `now` (for deterministic tests); defaults to the current instant. */
  nowISO?: string;
}

export function StateTimeline({ nodeTitle, events, nowISO }: StateTimelineProps) {
  const theme = useMapTheme();
  const editorial = theme.mode === "editorial";
  const c = theme.palette;
  const model = buildStateTimeline(events, nowISO);
  const x = (frac: number) => AXIS_X0 + frac * (AXIS_X1 - AXIS_X0);

  // Colour resolvers fork on theme: editorial pulls the band wash from the
  // state's warm `chipBg`, the dot/label from the saturated `dot`/`fg`, and the
  // glyph from `theme.stateStyle` (⚠ amber for blocked). Legacy keeps the
  // shipped helpers verbatim — byte-identical fills, axis greys, glyphs.
  const bandOf = (state: TaskState) =>
    editorial ? theme.stateStyle(state).chipBg : bandFill(state);
  const dotOf = (state: TaskState) =>
    editorial
      ? (theme.stateStyle(state).dot ?? theme.stateStyle(state).fg)
      : state === "planned"
        ? "#9CA3AF"
        : undefined;
  const glyphOf = (state: TaskState) =>
    editorial ? theme.stateStyle(state).glyph : stateGlyph(state);
  const axisGrey = editorial ? c.faint : "#9CA3AF";
  const monoText = editorial ? { fontFamily: theme.fonts.mono } : undefined;

  return (
    <section
      data-testid="state-timeline"
      className="rounded-lg border p-3"
      style={{
        borderColor: editorial ? c.rule : PALETTE.softBorder,
        backgroundColor: editorial ? c.cardBg : "#FFFFFF",
      }}
    >
      <div
        className={editorial ? "mb-1 text-[15px]" : "mb-1 text-[12px] font-bold"}
        style={
          editorial
            ? { color: c.ink, fontFamily: theme.fonts.serif, fontStyle: "italic" }
            : { color: PALETTE.ink }
        }
      >
        “{nodeTitle}” — state over time
      </div>

      {!model.hasSpan ? (
        <div
          data-testid="state-timeline-thin"
          className="px-1 py-2 text-[11px]"
          style={{ color: editorial ? c.faint : PALETTE.faint }}
        >
          This task hasn’t changed state yet.
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          role="img"
          aria-label={`State timeline for ${nodeTitle}`}
          style={{ display: "block" }}
        >
          {/* axis + arrowhead */}
          <line
            x1={AXIS_X0}
            y1={AXIS_Y}
            x2={AXIS_X1}
            y2={AXIS_Y}
            stroke={editorial ? c.rule2 : PALETTE.softBorder}
            strokeWidth={1.5}
          />
          <path
            d={`M${AXIS_X1 - 6} ${AXIS_Y - 4} L${AXIS_X1 + 4} ${AXIS_Y} L${AXIS_X1 - 6} ${AXIS_Y + 4}`}
            fill={editorial ? c.rule2 : PALETTE.softBorder}
          />

          {/* date ticks */}
          {model.ticks.map((t, i) => {
            const last = i === model.ticks.length - 1;
            return (
              <g key={`tick-${t.atISO}`}>
                <line
                  x1={x(t.frac)}
                  y1={AXIS_Y - 6}
                  x2={x(t.frac)}
                  y2={AXIS_Y + 6}
                  stroke={axisGrey}
                />
                <text
                  x={x(t.frac)}
                  y={AXIS_Y + 20}
                  fontSize={10}
                  fill={axisGrey}
                  textAnchor="middle"
                  style={monoText}
                >
                  {last ? "now" : formatDateTick(t.atISO)}
                </text>
              </g>
            );
          })}

          {/* state bands — one per span, tinted by the resolved theme */}
          {model.segments.map((seg) => {
            const x0 = x(seg.fromFrac);
            const w = Math.max(x(seg.toFrac) - x0, 0);
            if (w <= 0) return null;
            return (
              <rect
                key={`band-${seg.fromISO}`}
                data-testid="timeline-band"
                data-state={seg.state}
                x={x0}
                y={BAND_Y}
                width={w}
                height={BAND_H}
                rx={3}
                fill={bandOf(seg.state)}
              />
            );
          })}

          {/* event dots */}
          {model.markers.map((m, i) => (
            <circle
              key={`dot-${m.atISO}-${i}`}
              data-testid="timeline-dot"
              data-state={m.state}
              cx={x(m.frac)}
              cy={AXIS_Y - 8}
              r={i === 0 ? 5 : 6}
              fill={dotOf(m.state) ?? m.fg}
              stroke={editorial ? c.cardBg : "#FFFFFF"}
              strokeWidth={1.5}
            />
          ))}

          {/* captions above the dots ("created", "started", …) */}
          {model.markers.map((m, i) => (
            <text
              key={`cap-${m.atISO}-${i}`}
              x={x(m.frac)}
              y={BAND_Y - 13}
              fontSize={10}
              fill={dotOf(m.state) ?? m.fg}
              textAnchor="middle"
              style={monoText}
            >
              {m.label}
            </text>
          ))}

          {/* state labels below each band */}
          {model.segments.map((seg) => {
            const x0 = x(seg.fromFrac);
            const w = x(seg.toFrac) - x0;
            if (w <= 24) return null;
            return (
              <text
                key={`label-${seg.fromISO}`}
                data-testid="timeline-state-label"
                x={x0 + w / 2}
                y={H - 6}
                fontSize={10}
                fill={editorial ? theme.stateStyle(seg.state).fg : seg.fg}
                textAnchor="middle"
              >
                {glyphOf(seg.state)} {seg.label}
              </text>
            );
          })}
        </svg>
      )}

      {/* The wireframe's two framing cards (editorial only) — provenance is
          read factually ("started Monday after a gap"), NEVER as a judgment
          about a person. The "NEVER AS" card is the brand firewall, struck
          through, in calm amber (§A9 / brand firewall). */}
      {editorial && model.hasSpan && <FramingCards theme={theme} />}
    </section>
  );
}

/**
 * The "READ THIS AS" (sage-bordered, factual) / "NEVER AS" (amber wash,
 * struck-through) pair from B3.2. Generic copy about reading a trajectory —
 * carries no actor, count, or rank (§A9 / brand firewall).
 */
function FramingCards({ theme }: { theme: MapTheme }) {
  const c = theme.palette;
  const done = theme.stateStyle("done");
  const blocked = theme.stateStyle("blocked");
  return (
    <div
      data-testid="state-timeline-framing"
      className="mt-3 grid gap-3"
      style={{ gridTemplateColumns: "1fr 1fr" }}
    >
      <div
        data-testid="state-timeline-read-as"
        className="rounded-lg border p-3"
        style={{ borderColor: `${done.dot ?? done.fg}66`, backgroundColor: c.cardBg }}
      >
        <div
          className="text-[10px] font-semibold uppercase"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }}
        >
          Read this as
        </div>
        <div className="mt-1.5 text-[13px] italic" style={{ color: c.ink }}>
          “Started after a quiet stretch, then kept moving. Still in motion.”
        </div>
      </div>
      <div
        data-testid="state-timeline-never-as"
        className="rounded-lg border p-3"
        style={{ borderColor: `${blocked.dot ?? blocked.fg}66`, backgroundColor: blocked.chipBg }}
      >
        <div
          className="text-[10px] font-semibold uppercase"
          style={{ color: c.muted, fontFamily: theme.fonts.mono, letterSpacing: "0.08em" }}
        >
          Never as
        </div>
        <div
          className="mt-1.5 text-[13px] italic line-through"
          style={{ color: c.ink, textDecorationColor: "rgba(0,0,0,0.4)" }}
        >
          “They took too long to pick this up.”
        </div>
      </div>
    </div>
  );
}

/** Soft band fills (the SVG uses a lighter wash than the dot/text fg). */
function bandFill(state: string): string {
  switch (state) {
    case "in_progress":
      return "#EEF2FF";
    case "done":
      return "#ECFDF5";
    case "blocked":
      return "#FEF3C7";
    default:
      return "#E3E6EC";
  }
}

/** The state's glyph for the below-band label (mirrors `TASK_STATES`). */
function stateGlyph(state: string): string {
  switch (state) {
    case "in_progress":
      return "◐";
    case "done":
      return "●";
    case "blocked":
      return "⊘";
    default:
      return "○";
  }
}
